import { FC } from 'react';
import type { CustomChartConversationItem, CustomChartValueType } from '@/entities/dashboards';
import { cn } from '@/shared/lib';
import { getConversationLinkProps } from '../../conversationLink';
import { DASHBOARD_CLICKABLE_BLOCK_CLASS } from '../../utils';
import { chartDatePrecision, shownChartValue } from '../chartValues';
import { ChartCardShell } from '../ChartCardShell';

type Props = {
  title: string;
  items: CustomChartConversationItem[];
  /** Тип величины строки: по нему величина-дата показывается датой, а число — числом */
  valueType: CustomChartValueType;
  /**
   * Открытие диалога; `openInNewTab` — пользователь кликнул с shift. Не задано — строки нажатий
   * не принимают и адреса диалога не несут.
   */
  onOpenConversation?: (conversationId: number, openInNewTab: boolean) => void;
};

/** Начертание строки списка: у строки с диалогом добавляется отклик на наведение. */
const ROW_CLASS = 'flex items-center gap-3 rounded-lg border bg-card p-3 no-underline text-inherit';

/**
 * Список диалогов: перечень отдельных разговоров с величиной каждого.
 *
 * Строка с разговором ведёт в него ссылкой — правило перехода общее для дашборда, см.
 * {@link getConversationLinkProps}. Строка, за которой разговор не назван, остаётся обычной
 * строкой: переходить по ней некуда.
 *
 * С погашенным переходом ссылок в списке нет вовсе: в режиме правки состава уходить с дашборда
 * некуда, и адрес диалога у строки только обманывал бы.
 */
export const ConversationListChartView: FC<Props> = ({ title, items, valueType, onOpenConversation }) => {
  // Точность времени берётся по величинам всех строк сразу — соседние строки написаны одинаково.
  const precision = chartDatePrecision(
    items.map((item) => item.value),
    valueType,
  );

  return (
    <ChartCardShell title={title}>
      <div className="flex flex-col gap-2">
        {items.map((item, index) => {
          const { conversationId } = item;
          const value = shownChartValue(item.value, valueType, precision);

          if (conversationId === null || !onOpenConversation) {
            // Ключ по месту в списке: строка без разговора ничем другим не адресуется, а с
            // погашенным переходом по месту адресуются и все прочие строки.
            return (
              <div key={`${item.title}-${index}`} className={ROW_CLASS}>
                <span className="min-w-0 flex-1 truncate text-sm">{item.title}</span>
                <span className="text-sm font-bold">{value}</span>
              </div>
            );
          }

          return (
            <a
              key={conversationId}
              {...getConversationLinkProps(conversationId, onOpenConversation)}
              className={cn(ROW_CLASS, DASHBOARD_CLICKABLE_BLOCK_CLASS)}
            >
              <span className="min-w-0 flex-1 truncate text-sm">{item.title}</span>
              <span className="text-sm font-bold">{value}</span>
            </a>
          );
        })}
      </div>
    </ChartCardShell>
  );
};
